import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import Button from "../../Components/Button";
import { Typography } from "../../Components/Typography";
import CreateIdea from "../../Components/CreateIdea";
import IdeaFlowCard from "../../Components/IdeaFlowCard";
import Card from "../../Components/Card";
import { useAuth } from "../../Context/authContext";
import { getIdeasByUserId, createIdeaUser, getUsersByRole, createIdeaMentorPreference, getAllConfigurations, activateIdea, getIdeaById, notifyNoMentorMatch } from "../../Utils/apiServices";
import {
  extractIdeasData,
  sortIdeasByStatus,
  mapIdeaRecordToCardData,
  getActiveStageIndexFromCurrentStep,
} from "./utils";
import toast from "../../Utils/toastManager";

// Fallback limits used until the configuration is loaded (or if it's unavailable).
const DEFAULT_MAX_ACTIVE_IDEAS_PER_STUDENT = 2;
const DEFAULT_MAX_TOTAL_IDEAS_PER_STUDENT = 10;
const ACTIVE_IDEA_STATUSES = new Set(["PENDING", "ACTIVE"]);

const StudentIdeasReview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [showMentorSelection, setShowMentorSelection] = useState(false);
  const [showPreferencesSubmitted, setShowPreferencesSubmitted] = useState(false);
  const [mentors, setMentors] = useState([]);
  const [selectedMentors, setSelectedMentors] = useState([]);
  const [loadingMentors, setLoadingMentors] = useState(false);
  const [submittedInnovationCategory, setSubmittedInnovationCategory] = useState(null);
  const [showCreateIdea, setShowCreateIdea] = useState(false);
  // The saved draft currently being continued (null when creating a new idea).
  const [editingDraft, setEditingDraft] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [currentIdeaId, setCurrentIdeaId] = useState(null);
  // True when the just-created idea was saved as Inactive (active limit reached).
  const [createdIdeaInactive, setCreatedIdeaInactive] = useState(false);
  // True when no mentor matched the idea's category at submit time (admin notified).
  const [noMentorMatch, setNoMentorMatch] = useState(false);
  const [activatingIdeaId, setActivatingIdeaId] = useState(null);
  // Idea limits are driven by the admin Configuration (active_idea / overall_idea).
  const [maxActiveIdeas, setMaxActiveIdeas] = useState(DEFAULT_MAX_ACTIVE_IDEAS_PER_STUDENT);
  const [maxTotalIdeas, setMaxTotalIdeas] = useState(DEFAULT_MAX_TOTAL_IDEAS_PER_STUDENT);

  const mapStudentIdeasToUI = (ideasData) =>
    ideasData.map(mapIdeaRecordToCardData).filter(Boolean);

  // Limits only apply to ideas the student owns (PRIMARY_STUDENT) — ideas
  // where they were added as a SECONDARY_STUDENT collaborator don't count.
  const ownedIdeas = ideas.filter((idea) => {
    const role = idea?._original?.role;
    return !role || role === "PRIMARY_STUDENT";
  });
  // Rejected ideas don't count toward the total limit; drafts aren't submitted
  // yet, so they don't count either (matches the backend quota logic).
  const totalIdeasCount = ownedIdeas.filter(
    (idea) =>
      !["REJECTED", "DRAFT"].includes(String(idea?.status || "").toUpperCase())
  ).length;
  const activeIdeasCount = ownedIdeas.filter((idea) =>
    ACTIVE_IDEA_STATUSES.has(String(idea?.status || "").toUpperCase())
  ).length;
  const hasReachedTotalIdeaLimit = totalIdeasCount >= maxTotalIdeas;
  const hasReachedActiveIdeaLimit = activeIdeasCount >= maxActiveIdeas;
  const canActivateInactiveIdea = activeIdeasCount < maxActiveIdeas;
  // Creation is only blocked at the total limit; past the active limit ideas
  // are still allowed but the backend saves them as Inactive.
  const createIdeaBlockedReason = hasReachedTotalIdeaLimit
    ? `You can create up to ${maxTotalIdeas} ideas (excluding rejected ones) per innovator account.`
    : "";
  const inactiveCreationNotice =
    !hasReachedTotalIdeaLimit && hasReachedActiveIdeaLimit
      ? `You already have ${maxActiveIdeas} active ideas (Pending or Active). New ideas will be saved as Inactive and won't be sent to mentors until you activate them.`
      : "";

  const fetchAndMapIdeas = useCallback(async (userId, setLoadingState = true) => {
    if (!userId) {
      if (setLoadingState) setLoading(false);
      return;
    }

    try {
      if (setLoadingState) setLoading(true);
      const response = await getIdeasByUserId(userId);
      const ideasData = extractIdeasData(response);
      const mappedIdeas = sortIdeasByStatus(mapStudentIdeasToUI(ideasData));
      setIdeas(mappedIdeas);
    } catch (error) {
      console.error("Error fetching ideas:", error);
      setIdeas([]);
    } finally {
      if (setLoadingState) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "student") {
      fetchAndMapIdeas(user?.id);
    }
  }, [user?.id, user?.role, fetchAndMapIdeas]);

  // Load idea limits from the admin Configuration (active_idea / overall_idea).
  useEffect(() => {
    const fetchIdeaLimits = async () => {
      try {
        const response = await getAllConfigurations();
        const payload = response?.data?.data ?? response?.data;
        const config = Array.isArray(payload) ? payload[0] : payload;
        if (response?.success && config) {
          const activeLimit = Number(config.active_idea);
          const totalLimit = Number(config.overall_idea);
          if (!Number.isNaN(activeLimit) && config.active_idea != null && config.active_idea !== "") {
            setMaxActiveIdeas(activeLimit);
          }
          if (!Number.isNaN(totalLimit) && config.overall_idea != null && config.overall_idea !== "") {
            setMaxTotalIdeas(totalLimit);
          }
        }
      } catch (error) {
        console.error("Error fetching idea limits configuration:", error);
        // Keep default fallback limits on failure.
      }
    };
    fetchIdeaLimits();
  }, []);

  const handleIdeaSubmit = async (formData, response, meta = {}) => {
    // Store the innovation category for mentor filtering
    if (formData?.innovationCategory) {
      setSubmittedInnovationCategory(formData.innovationCategory);
    }

    // Extract the created/updated idea from the response
    let ideaId = null;
    let createdIdea = null;
    if (response?.success && response.data) {
      // Try different possible response structures
      if (response.data?.data?.id) {
        createdIdea = response.data.data;
      } else if (response.data?.id) {
        createdIdea = response.data;
      } else if (response.data?.data?.data?.id) {
        createdIdea = response.data.data.data;
      }
      ideaId = createdIdea?.id ?? null;
    }
    // A submitted draft already has its id; fall back to it.
    ideaId = ideaId ?? editingDraft?.id ?? null;
    const isInactiveIdea =
      String(createdIdea?.status || "").toUpperCase() === "INACTIVE";
    // When igniting a saved draft, the PRIMARY_STUDENT link already exists.
    const linkAlreadyExists = Boolean(meta.fromDraft || editingDraft);

    if (!ideaId || !user?.id) return;

    try {
      if (!linkAlreadyExists) {
        await createIdeaUser({
          idea_id: ideaId,
          user_id: user.id,
          role: "PRIMARY_STUDENT",
        });
      }
      // Inactive ideas skip mentor preferences — they are not sent to
      // mentors until the student activates them.
      setCreatedIdeaInactive(isInactiveIdea);

      // For active (Pending) ideas, check at submit time whether any mentor
      // matches the idea's innovation category. If none, notify the admin and
      // skip mentor selection; otherwise continue the normal recommendation flow.
      let noMatch = false;
      if (!isInactiveIdea) {
        const category = Array.isArray(formData?.innovationCategory)
          ? formData.innovationCategory.join(",")
          : formData?.innovationCategory || null;
        try {
          const mentorRes = await getUsersByRole(
            "mentor",
            category ? { industry: category } : {}
          );
          const list = Array.isArray(mentorRes?.data?.data)
            ? mentorRes.data.data
            : Array.isArray(mentorRes?.data)
            ? mentorRes.data
            : [];
          noMatch = list.length === 0;
        } catch (mentorErr) {
          console.error("Error checking mentor availability:", mentorErr);
          noMatch = false; // On lookup failure, fall back to the normal flow.
        }
        if (noMatch) {
          try {
            await notifyNoMentorMatch(ideaId);
          } catch (notifyErr) {
            console.error("Error notifying admin of no mentor match:", notifyErr);
          }
        }
      }
      setNoMentorMatch(noMatch);
      // Only keep the idea id for mentor selection when we'll actually show it.
      setCurrentIdeaId(isInactiveIdea || noMatch ? null : ideaId);

      // Show success screen and close the form
      setShowSuccessScreen(true);
      setShowCreateIdea(false);
      setEditingDraft(null);
      // Refetch ideas
      await fetchAndMapIdeas(user.id);
    } catch (error) {
      console.error("Error finalizing idea submission:", error);
      // Continue even if the link fails - the idea was already saved.
    }
  };

  // Called after CreateIdea persists the form as a DRAFT. For a brand-new draft
  // we create the PRIMARY_STUDENT link so it appears in "My Ideas".
  const handleIdeaDraftSaved = async (response, meta = {}) => {
    const savedIdea =
      response?.data?.data ?? response?.data ?? null;
    const ideaId = savedIdea?.id ?? meta.ideaId ?? editingDraft?.id ?? null;

    if (meta.isNew && ideaId && user?.id) {
      try {
        await createIdeaUser({
          idea_id: ideaId,
          user_id: user.id,
          role: "PRIMARY_STUDENT",
        });
      } catch (error) {
        console.error("Error linking draft to user:", error);
      }
    }

    toast.success("Draft saved. You can continue it anytime from your ideas.");
    setShowCreateIdea(false);
    setEditingDraft(null);
    if (user?.id) await fetchAndMapIdeas(user.id, false);
  };

  // Open the create form prefilled with a saved draft's content.
  const handleContinueDraft = async (ideaCard) => {
    if (!ideaCard?.id) return;
    setLoadingDraft(true);
    try {
      const res = await getIdeaById(ideaCard.id);
      const full =
        res?.data?.data ??
        res?.data ??
        ideaCard._original?.idea ??
        ideaCard._original ??
        { id: ideaCard.id };
      setEditingDraft(full);
    } catch (error) {
      console.error("Error loading draft:", error);
      setEditingDraft(
        ideaCard._original?.idea ?? ideaCard._original ?? { id: ideaCard.id }
      );
    } finally {
      setLoadingDraft(false);
      setShowCreateIdea(true);
    }
  };

  // Fetch mentors
  const fetchMentors = async (innovationCategory = null) => {
    try {
      setLoadingMentors(true);
      
      // Build query parameters
      const params = {};
      if (innovationCategory) {
        params.industry = innovationCategory;
      }
      
      const response = await getUsersByRole("mentor", params);
      
      let mentorsData = [];
      if (response.success) {
        if (Array.isArray(response.data?.data)) {
          mentorsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          mentorsData = response.data;
        }
      }

      // Filter only approved mentors
      const approvedMentors = mentorsData.filter(mentor => mentor.mentor_status === "approved");
      setMentors(approvedMentors);
    } catch (error) {
      console.error("Error fetching mentors:", error);
      setMentors([]);
    } finally {
      setLoadingMentors(false);
    }
  };

  // Handle mentor selection
  const handleMentorSelect = (mentorId) => {
    setSelectedMentors((prev) => {
      const isSelected = prev.some(m => m.id === mentorId);
      
      if (isSelected) {
        // Remove mentor if already selected
        return prev.filter(m => m.id !== mentorId).map((m, index) => ({
          ...m,
          preference: index + 1
        }));
      } else {
        // Add mentor if not selected and less than 3
        if (prev.length < 3) {
          const newMentor = mentors.find(m => m.id === mentorId);
          return [...prev, { ...newMentor, preference: prev.length + 1 }].map((m, index) => ({
            ...m,
            preference: index + 1
          }));
        }
        return prev;
      }
    });
  };

  // Submit mentor preferences
  const handleSubmitPreferences = async () => {
    if (!currentIdeaId || !user?.id || selectedMentors.length === 0) {
      console.error("Missing required data for mentor preferences");
      return;
    }

    try {
      // Submit preferences for each selected mentor
      const preferencePromises = selectedMentors.map((mentor) =>
        ({
          idea_id: currentIdeaId,
          student_id: user.id,
          mentor_id: mentor.id,
          preference_order: mentor.preference
        })
      );
      const response = await createIdeaMentorPreference(preferencePromises);
      if (response.success) {
        console.log("Mentor preferences submitted successfully");
        setShowMentorSelection(false);
        setShowPreferencesSubmitted(true);
        // Reset state
        setSelectedMentors([]);
        setCurrentIdeaId(null);
      }
      else {
        console.error("Mentor preferences failed to submit");
        toast.error(response?.error || "Failed to submit mentor preferences. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting mentor preferences:", error);
      // Surface backend validation errors (e.g. a mentor being at their
      // maximum active-mentorship capacity) to the student.
      toast.error(error?.error || "Failed to submit mentor preferences. Please try again.");
    }
  };

  // Activate an Inactive idea (allowed while under the active-idea limit),
  // then send the student through mentor selection so it reaches mentors.
  const handleActivateIdea = async (ideaCard) => {
    if (activatingIdeaId || !ideaCard?.id) return;
    try {
      setActivatingIdeaId(ideaCard.id);
      const response = await activateIdea(ideaCard.id);
      if (response?.success) {
        await fetchAndMapIdeas(user?.id, false);
        const original = ideaCard._original?.idea ?? ideaCard._original ?? {};
        setCurrentIdeaId(ideaCard.id);
        setShowMentorSelection(true);
        await fetchMentors(original.innovation_category || null);
      } else {
        toast.error(response?.error || "Failed to activate the idea. Please try again.");
      }
    } catch (error) {
      toast.error(error?.error || "Failed to activate the idea. Please try again.");
    } finally {
      setActivatingIdeaId(null);
    }
  };

  // The student's chosen mentor preferences for an idea (excludes secondary-mentor
  // invites), read from the idea listing. Returns null when the listing doesn't
  // carry preference data (older API), so we don't guess and show false actions.
  const getStudentMentorPreferences = (ideaCard) => {
    const prefs =
      ideaCard?._original?.idea?.mentorPreferences ??
      ideaCard?._original?.mentorPreferences;
    if (!Array.isArray(prefs)) return null;
    return prefs.filter((p) => p.invited_by == null);
  };

  // A PENDING idea the student owns but never picked mentors for — it's stuck
  // with no mentor assigned until the student selects their preferences.
  const ideaNeedsMentorSelection = (ideaCard) => {
    const status = String(ideaCard?.status || "").toUpperCase();
    const isOwner =
      !ideaCard?._original?.role || ideaCard._original.role === "PRIMARY_STUDENT";
    const studentPrefs = getStudentMentorPreferences(ideaCard);
    return (
      status === "PENDING" &&
      isOwner &&
      studentPrefs !== null &&
      studentPrefs.length === 0
    );
  };

  // Open the mentor selection screen for a pending idea that has no preferences yet.
  const handleSelectMentors = async (ideaCard) => {
    if (!ideaCard?.id) return;
    const original = ideaCard._original?.idea ?? ideaCard._original ?? {};
    setSelectedMentors([]);
    setCurrentIdeaId(ideaCard.id);
    setSubmittedInnovationCategory(original.innovation_category || null);
    setShowMentorSelection(true);
    await fetchMentors(original.innovation_category || null);
  };

  // Get mentor title/role
  const getMentorTitle = (mentor) => {
    if (mentor.profession) return mentor.profession;
    if (mentor.organization) return mentor.organization;
    if (mentor.functional_experience) return mentor.functional_experience;
    return "Mentor";
  };

  // Get mentor description
  const getMentorDescription = (mentor) => {
    const parts = [];
    if (mentor.city && mentor.country) {
      parts.push(`${mentor.city}-based mentor`);
    } else if (mentor.country) {
      parts.push(`${mentor.country}-based mentor`);
    }
    if (mentor.industry) {
      parts.push(`specializing in ${mentor.industry}`);
    } else if (mentor.functional_experience) {
      parts.push(`specializing in ${mentor.functional_experience}`);
    }
    if (mentor.bio) {
      parts.push(mentor.bio);
    }
    return parts.length > 0 ? parts.join(". ") + "." : "Experienced mentor ready to guide you.";
  };

  // Show preferences submitted screen
  if (showPreferencesSubmitted) {
    return (
      <div className="flex flex-col w-full h-auto min-h-screen bg-white">
        {/* Header */}
        <div className="px-8 pt-8">
          <Typography as="h1" className="text-2xl font-bold text-primary-900">
            Idea Submitted
          </Typography>
        </div>

        {/* Success Content */}
        <div className="flex flex-col items-center justify-center flex-1 px-8 py-20">
          {/* Success Icon */}
          <div className="relative mb-8 flex items-center justify-center">
            {/* Concentric squares effect - outer to inner */}
            <div className="absolute w-32 h-32 rounded-lg border-2 border-slate-200 opacity-20"></div>
            <div className="absolute w-28 h-28 rounded-lg border-2 border-slate-200 opacity-30"></div>
            <div className="absolute w-24 h-24 rounded-lg border-2 border-slate-200 opacity-50"></div>
            {/* Main square with checkmark */}
            <div className="relative w-20 h-20 rounded-lg bg-slate-50 flex items-center justify-center border border-slate-200">
              <Icon 
                icon="lucide:check" 
                className="w-10 h-10 text-slate-400"
              />
            </div>
          </div>

          {/* Heading */}
          <Typography 
            as="h2" 
            className="text-3xl font-bold text-primary-900 mb-4 text-center"
          >
            Your Mentor Preferences have been submitted.
          </Typography>

          {/* Description */}
          <Typography 
            as="p" 
            className="text-lg text-tertiary-600 mb-8 text-center max-w-md"
          >
            You will receive a confirmation mail as soon as a mentor has approved mentorship.
          </Typography>

          {/* Button */}
          <Button
            variant="primary"
            size="large"
            className="bg-[#7F56D9] hover:bg-purple-700 text-white px-8 py-3 text-base font-medium rounded-lg"
            onClick={() => {
              setShowPreferencesSubmitted(false);
              // Refresh ideas to show the new one
              if (user?.id) {
                fetchAndMapIdeas(user.id, false);
              }
            }}
          >
            View Ideas
          </Button>
        </div>
      </div>
    );
  }

  // Show mentor selection screen
  if (showMentorSelection) {
    return (
      <div className="flex flex-col w-full h-auto min-h-screen bg-white px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Typography as="h1" className="text-2xl font-bold text-primary-900 mb-2">
            Idea Submitted
          </Typography>
          <Typography as="p" className="text-md text-tertiary-600">
            Select the mentor cards based on your mentor preference.
          </Typography>
        </div>

        {/* Loading state */}
        {loadingMentors ? (
          <div className="flex items-center justify-center py-20">
            <Typography as="p" className="text-md text-tertiary-600">
              Loading mentors...
            </Typography>
          </div>
        ) : mentors.length > 0 ? (
          <>
            {/* Mentor Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {mentors.map((mentor) => {
                const isSelected = selectedMentors.some(m => m.id === mentor.id);
                const selectedMentor = selectedMentors.find(m => m.id === mentor.id);
                const preference = selectedMentor?.preference;

                return (
                  <Card
                    key={mentor.id}
                    className={`relative flex flex-col gap-4 p-6 border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? "border-[#7F56D9] bg-purple-50" 
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    onClick={() => handleMentorSelect(mentor.id)}
                  >
                    {/* Preference Number Badge */}
                    {isSelected && (
                      <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#7F56D9] flex items-center justify-center">
                        <Typography as="span" className="text-white text-sm font-semibold">
                          {preference}
                        </Typography>
                      </div>
                    )}

                    {/* Profile Picture */}
                    <div className="flex justify-center">
                      <div className="w-24 h-24 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center">
                        {mentor.profile_picture_url ? (
                          <img
                            src={mentor.profile_picture_url}
                            alt={`${mentor.first_name} ${mentor.last_name}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Icon icon="lucide:user" className="w-12 h-12 text-slate-400" />
                        )}
                      </div>
                    </div>

                    {/* Name */}
                    <Typography as="h3" className="text-xl font-bold text-primary-900 text-center">
                      {mentor.first_name} {mentor.last_name}
                    </Typography>

                    {/* Title */}
                    <Typography as="p" className="text-[#7F56D9] font-medium text-center">
                      {getMentorTitle(mentor)}
                    </Typography>

                    {/* Description */}
                    <Typography as="p" className="text-sm text-tertiary-600 text-center">
                      {getMentorDescription(mentor)}
                    </Typography>

                    {/* Social Icons */}
                    <div className="flex justify-center gap-3 pt-2">
                      {mentor.linkedin_url && (
                        <a
                          href={mentor.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-slate-400 hover:text-[#7F56D9] transition-colors"
                        >
                          <Icon icon="mdi:linkedin" className="w-5 h-5" />
                        </a>
                      )}
                      {mentor.x_url && (
                        <a
                          href={mentor.x_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-slate-400 hover:text-[#7F56D9] transition-colors"
                        >
                          <Icon icon="mdi:twitter" className="w-5 h-5" />
                        </a>
                      )}
                      {mentor.github_url && (
                        <a
                          href={mentor.github_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-slate-400 hover:text-[#7F56D9] transition-colors"
                        >
                          <Icon icon="mdi:github" className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Bottom Action Section */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1">
                <Typography as="p" className="text-base font-bold text-primary-900">
                  Based on your preferences, a mentor will be assigned to you soon
                </Typography>
                <Typography as="p" className="text-sm text-tertiary-600">
                  You will receive a confirmation mail as soon as a mentor has approved mentorship.
                </Typography>
              </div>
              <Button
                variant="primary"
                size="medium"
                className="bg-[#7F56D9] hover:bg-purple-700 text-white px-6 py-2 text-sm font-medium rounded-lg"
                onClick={handleSubmitPreferences}
                disabled={selectedMentors.length === 0}
              >
                Submit Preferences
              </Button>
            </div>
          </>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <Icon icon="lucide:users" className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <Typography as="h3" className="text-xl font-bold text-primary-900 mb-2">
              No Mentors Available
            </Typography>
            <Typography as="p" className="text-base text-tertiary-600">
              There are currently no mentors available in your selected innovation category. The IgniteNext team will contact you shortly to assist with mentor assignment.
            </Typography>
          </div>
        )}
      </div>
    );
  }

  // Show success screen
  if (showSuccessScreen) {
    return (
      <div className="flex flex-col w-full h-auto min-h-screen bg-white">
        {/* Header */}
        <div className="px-8 pt-8">
          <Typography as="h1" className="text-2xl font-bold text-primary-900">
            Idea Submitted
          </Typography>
        </div>

        {/* Success Content */}
        <div className="flex flex-col items-center justify-center flex-1 px-8 py-20">
          {/* Success Icon */}
          <div className="relative mb-8 flex items-center justify-center">
            {/* Concentric squares effect - outer to inner */}
            <div className="absolute w-32 h-32 rounded-lg border-2 border-slate-200 opacity-20"></div>
            <div className="absolute w-28 h-28 rounded-lg border-2 border-slate-200 opacity-30"></div>
            <div className="absolute w-24 h-24 rounded-lg border-2 border-slate-200 opacity-50"></div>
            {/* Main square with checkmark */}
            <div className="relative w-20 h-20 rounded-lg bg-slate-50 flex items-center justify-center border border-slate-200">
              <Icon 
                icon="lucide:check" 
                className="w-10 h-10 text-slate-400"
              />
            </div>
          </div>

          {/* Heading */}
          <Typography 
            as="h2" 
            className="text-3xl font-bold text-primary-900 mb-4 text-center"
          >
            Your Idea has been submitted.
          </Typography>

          {/* Description */}
          <Typography
            as="p"
            className="text-lg text-tertiary-600 mb-8 text-center max-w-md"
          >
            {createdIdeaInactive
              ? `You already have ${maxActiveIdeas} active ideas, so this idea was saved as Inactive. It won't be sent to mentors for review until you activate it after one of your active ideas is decided.`
              : noMentorMatch
              ? "We couldn't find a mentor matching your innovation category right now. The IgniteNext team has been notified and will help assign a mentor for your idea."
              : "Based on your input, we're going to recommend a few mentors we think might be a good fit."}
          </Typography>

          {/* Button */}
          <Button
            variant="primary"
            size="large"
            className="bg-[#7F56D9] hover:bg-purple-700 text-white px-8 py-3 text-base font-medium rounded-lg"
            onClick={async () => {
              setShowSuccessScreen(false);
              if (createdIdeaInactive) {
                setCreatedIdeaInactive(false);
                return;
              }
              if (noMentorMatch) {
                setNoMentorMatch(false);
                return;
              }
              setShowMentorSelection(true);
              // Fetch mentors with innovation category filter
              await fetchMentors(Array.isArray(submittedInnovationCategory) ? submittedInnovationCategory.join(",") : submittedInnovationCategory);
            }}
          >
            {createdIdeaInactive || noMentorMatch ? "View Ideas" : "View Recommendations"}
          </Button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex flex-col w-full h-auto gap-8 bg-primary px-8 py-8">
        <div className="flex items-center justify-center py-20">
          <Typography as="p" className="text-md text-tertiary-600">
            Loading ideas...
          </Typography>
        </div>
      </div>
    );
  }

  // If showCreateIdea is true, show the Create Idea form
  if (showCreateIdea) {
    return (
      <CreateIdea
        onSubmit={handleIdeaSubmit}
        onDraftSaved={handleIdeaDraftSaved}
        initialIdea={editingDraft}
        isSubmitDisabled={Boolean(createIdeaBlockedReason)}
        submitDisabledReason={createIdeaBlockedReason}
      />
    );
  }

  // If student has no ideas and not loading, show the Create Idea form
  if (ideas.length === 0 && !loading) {
    return (
      <CreateIdea
        onSubmit={handleIdeaSubmit}
        onDraftSaved={handleIdeaDraftSaved}
        initialIdea={editingDraft}
        isSubmitDisabled={Boolean(createIdeaBlockedReason)}
        submitDisabledReason={createIdeaBlockedReason}
      />
    );
  }

  // Show review interface when there are ideas
  return (
    <div className="flex flex-col w-full h-auto gap-8 bg-primary px-8 py-8">
      {/* Header Section */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between w-full">
        <div className="flex flex-col gap-2 items-start justify-start">
          <Typography as="h1" className="text-2xl font-bold text-primary-900">
            Review Ideas
          </Typography>
          <Typography as="p" className="text-md text-tertiary-600">
            Review assigned ideas, view AI summaries, and accept or decline
            assignments
          </Typography>
        </div>
        <Button
          variant="primary"
          size="medium"
          startIcon={<Icon icon="lucide:plus" className="h-5 w-5" />}
          disabled={Boolean(createIdeaBlockedReason) || loadingDraft}
          onClick={() => {
            setEditingDraft(null);
            setShowCreateIdea(true);
          }}
        >
          Create Idea
        </Button>
      </div>
      {createIdeaBlockedReason && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          <Icon icon="lucide:alert-triangle" className="h-4 w-4" />
          <span>{createIdeaBlockedReason}</span>
        </div>
      )}
      {inactiveCreationNotice && (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          <Icon icon="lucide:info" className="h-4 w-4" />
          <span>{inactiveCreationNotice}</span>
        </div>
      )}

      {/* Ideas Grid */}
      {ideas.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Icon icon="lucide:lightbulb" className="w-16 h-16 text-slate-400 mb-4" />
          <Typography as="p" className="text-lg font-semibold text-primary-900 mb-2">
            No ideas to review
          </Typography>
          <Typography as="p" className="text-md text-tertiary-600">
            You haven't created any ideas yet. Click "Create Idea" to get started.
          </Typography>
        </div>
      ) : (
        <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {ideas.map((idea) => (
            <IdeaFlowCard
              key={idea.id}
              idea={idea}
              onContinue={
                String(idea.status || "").toUpperCase() === "DRAFT" &&
                (!idea._original?.role || idea._original.role === "PRIMARY_STUDENT")
                  ? () => handleContinueDraft(idea)
                  : undefined
              }
              onSelectMentors={
                ideaNeedsMentorSelection(idea)
                  ? () => handleSelectMentors(idea)
                  : undefined
              }
              onActivate={
                String(idea.status || "").toUpperCase() === "INACTIVE" &&
                canActivateInactiveIdea &&
                (!idea._original?.role || idea._original.role === "PRIMARY_STUDENT")
                  ? () => handleActivateIdea(idea)
                  : undefined
              }
              activating={activatingIdeaId === idea.id}
              onClick={() =>
                String(idea.status || "").toUpperCase() === "DRAFT"
                  ? handleContinueDraft(idea)
                  : navigate(`/ideas/${idea.id}`, {
                      state: {
                        initialStage: getActiveStageIndexFromCurrentStep(idea.currentStep ?? 1),
                      },
                    })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentIdeasReview;

